// frontend-react/src/pages/BlogListPage.js
import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import BlogCard from "../components/Blog/BlogCard";
import AdBanner from "../components/Ads/AdBanner";
import PageHeader from "../components/Layout/HeaderContent";
import { blogService, categoryService } from "../api/services";
import { safeArray } from "../utils/validation";

const BlogListPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await categoryService.getCategories();
        setCategories(safeArray(res.data));
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch posts
  const fetchPosts = useCallback(async (pageNum, category, search) => {
    try {
      setLoading(true);
      let res;
      
      if (category !== "all") {
        res = await blogService.getPostsByCategory(category, pageNum);
      } else if (search) {
        res = await blogService.searchPosts(search, pageNum);
      } else {
        res = await blogService.getPublishedPosts(pageNum);
      }
      
      const newPosts = safeArray(res.data.results || res.data);
      setPosts(newPosts);
      
      // Calculate total pages if count is available
      if (res.data.count) {
        setTotalPages(Math.ceil(res.data.count / 10)); // Assuming 10 items per page
      }
      
      // Scroll to top on page change
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts(page, selectedCategory, searchQuery);
  }, [page, selectedCategory, searchQuery, fetchPosts]);

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <>
      <PageHeader position="sticky" sx={{ top: 0, zIndex: 1100 }} />
      
      <Container maxWidth="xl" sx={{ py: 6, px: { xs: 2, md: 4 } }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" fontWeight="900" gutterBottom>
            📚 Todos los Artículos
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto', mb: 4 }}>
            Explora nuestra colección completa de artículos sobre tecnología, innovación y tendencias digitales
          </Typography>
          
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 4 }}>
            <TextField
              placeholder="Buscar artículos..."
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Categoría</InputLabel>
              <Select
                value={selectedCategory}
                label="Categoría"
                onChange={handleCategoryChange}
              >
                <MenuItem value="all">Todas las categorías</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.slug} value={cat.slug}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          {selectedCategory !== "all" && (
            <Chip 
              label={`Filtrando por: ${categories.find(c => c.slug === selectedCategory)?.name || selectedCategory}`}
              onDelete={() => setSelectedCategory("all")}
              color="primary"
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </Box>

        {/* Ad Banner */}
        <AdBanner format="horizontal" placement="blog_top" />

        {/* Posts Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={4}>
              {posts.map((post, index) => {
                // Insert ad every 6 posts
                if (index > 0 && index % 6 === 0) {
                  return (
                    <React.Fragment key={`ad-${index}`}>
                      <Grid item xs={12}>
                        <AdBanner format="horizontal" placement={`blog_feed_${index}`} />
                      </Grid>
                      <Grid item xs={12} md={6} lg={4} key={post.id}>
                        <BlogCard post={post} />
                      </Grid>
                    </React.Fragment>
                  );
                }
                
                return (
                  <Grid item xs={12} md={6} lg={4} key={post.id}>
                    <BlogCard post={post} />
                  </Grid>
                );
              })}
            </Grid>

            {posts.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  No se encontraron artículos.
                </Typography>
              </Box>
            )}

            {/* Pagination */}
            {posts.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={handlePageChange} 
                  color="primary" 
                  size="large"
                  showFirstButton 
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default BlogListPage;
